import {useEffect, useState} from 'react'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

// Admins keep `admin_auth_token` in localStorage even while impersonating a client;
// send it as x-admin-token so the API's callerIsAdmin() authorizes the revert. The
// client JWT (Authorization) is added by the global axios interceptor.
const adminAuthHeaders = (): Record<string, string> => {
  const t = typeof window !== 'undefined' ? localStorage.getItem('admin_auth_token') : null
  return t ? {'x-admin-token': t} : {}
}

type DiffPart = {type: 'same' | 'add' | 'del'; text: string}

// Line-level LCS diff (git-style). `oldText` = current, `newText` = restore target;
// 'del' = a line the revert removes, 'add' = a line the revert brings back.
function diffLines(oldText: string, newText: string): DiffPart[] {
  const a = (oldText || '').split('\n')
  const b = (newText || '').split('\n')
  const n = a.length
  const m = b.length
  const dp: number[][] = Array.from({length: n + 1}, () => new Array(m + 1).fill(0))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }
  const out: DiffPart[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({type: 'same', text: a[i]}); i++; j++ }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({type: 'del', text: a[i]}); i++ }
    else { out.push({type: 'add', text: b[j]}); j++ }
  }
  while (i < n) { out.push({type: 'del', text: a[i]}); i++ }
  while (j < m) { out.push({type: 'add', text: b[j]}); j++ }
  return out
}

// Admin-only: bulk revert of post content to a prior version, by ACTUAL change event.
// The admin picks one of the real post-change date+times (not a range); the posts
// changed at that moment are listed with the previous version editable, and restoring
// snapshots the current version to each post's history first (so it's reversible).
// Account-wide (all campaigns).
export function ReverseByDatePanel() {
  const [events, setEvents] = useState<any[]>([])
  const [eventsLoaded, setEventsLoaded] = useState(false)
  const [selIdx, setSelIdx] = useState<number>(-1)
  const [items, setItems] = useState<any[]>([])
  const [edited, setEdited] = useState<Record<string, string>>({})
  const [sel, setSel] = useState<Record<string, boolean>>({})
  const [view, setView] = useState<Record<string, 'edit' | 'diff'>>({}) // per-post view: edit or git-style diff
  const [loading, setLoading] = useState(false)
  const [applying, setApplying] = useState(false)
  const [msg, setMsg] = useState('')

  const fmt = (d: string) => {
    try {
      return new Date(d).toLocaleString('he-IL')
    } catch {
      return d
    }
  }

  // Load the list of actual change events (date+time) once.
  useEffect(() => {
    ;(async () => {
      try {
        const res = await axios.post(`${API_URL}/post-revert/events`, {}, {headers: adminAuthHeaders()})
        setEvents(res.data?.events || [])
      } catch (e: any) {
        setMsg(e?.response?.data?.error || 'שגיאה בטעינת השינויים')
      } finally {
        setEventsLoaded(true)
      }
    })()
  }, [])

  // Load the posts changed in the selected event.
  const loadEvent = async (idx: number) => {
    setSelIdx(idx)
    setItems([])
    setEdited({})
    setSel({})
    setMsg('')
    if (idx < 0 || !events[idx]) return
    const ev = events[idx]
    setLoading(true)
    try {
      // Point-in-time restore: revert every post to how it was at this date.
      const res = await axios.post(`${API_URL}/post-revert/preview`, {at: ev.from}, {headers: adminAuthHeaders()})
      const its = res.data?.items || []
      setItems(its)
      const e: Record<string, string> = {}
      const s: Record<string, boolean> = {}
      its.forEach((i: any) => {
        e[i.version_id] = i.before
        s[i.version_id] = true
      })
      setEdited(e)
      setSel(s)
      setMsg(its.length ? `${its.length} מודעות ישוחזרו למצב של תאריך זה` : 'לא נמצאו מודעות לשחזור')
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'שגיאה בטעינה')
    } finally {
      setLoading(false)
    }
  }

  const apply = async () => {
    const updates = items
      .filter((i) => sel[i.version_id])
      .map((i) => ({post_id: i.post_id, content: edited[i.version_id] ?? i.before}))
    if (!updates.length) {
      setMsg('לא נבחרו מודעות')
      return
    }
    if (!window.confirm(`לשחזר ${updates.length} מודעות? הגרסה הנוכחית תישמר בהיסטוריה של כל פוסט (ניתן לשחזר חזרה).`)) return
    setApplying(true)
    try {
      const res = await axios.post(`${API_URL}/post-revert/apply`, {updates}, {headers: adminAuthHeaders()})
      const revertedIds = new Set(updates.map((u) => u.post_id))
      setItems((prev) => prev.filter((i) => !revertedIds.has(i.post_id)))
      setMsg(`שוחזרו ${res.data?.reverted || 0} מודעות`)
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'שגיאה בשחזור')
    } finally {
      setApplying(false)
    }
  }

  return (
    <>
      <div className='fs-8 text-muted mb-3'>
        בחר תאריך ושעה — כל הפוסטים בחשבון ישוחזרו למצב שבו היו באותו תאריך (ככל שחוזרים אחורה, יותר פוסטים מושפעים). המספר ליד כל תאריך = כמה פוסטים ישתנו. ניתן לערוך או להחריג כל פוסט לפני השחזור, והגרסה הנוכחית נשמרת בהיסטוריה כך שאפשר לשחזר חזרה.
      </div>
      <div className='d-flex gap-2 align-items-center mb-3 flex-wrap'>
        <select
          className='form-select form-select-sm'
          style={{maxWidth: 380}}
          value={selIdx}
          onChange={(e) => loadEvent(parseInt(e.target.value, 10))}
        >
          <option value={-1}>{eventsLoaded ? (events.length ? 'בחר שינוי (תאריך ושעה)…' : 'לא נמצאו שינויים') : 'טוען…'}</option>
          {events.map((ev, i) => (
            <option key={ev.key} value={i}>
              {fmt(ev.from)} — {ev.count} מודעות
            </option>
          ))}
        </select>
        <button className='btn btn-sm btn-danger' onClick={apply} disabled={applying || !items.length}>
          {applying ? 'משחזר…' : '↩️ שחזר נבחרים'}
        </button>
        {msg && <span className='text-muted fs-7'>{msg}</span>}
      </div>

      {loading ? (
        <div className='text-muted py-8 text-center'>טוען מודעות…</div>
      ) : items.length === 0 ? (
        <div className='text-muted py-8 text-center'>בחר שינוי מהרשימה למעלה.</div>
      ) : (
        items.map((it) => {
          const mode = view[it.version_id] || 'edit'
          const restore = edited[it.version_id] ?? it.before
          return (
          <div className='border rounded p-4 mb-4' key={it.version_id}>
            <div className='d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2'>
              <label className='form-check form-check-sm d-flex align-items-center gap-2 m-0' title='בטל סימון כדי להחריג את הפוסט הזה מהשחזור'>
                <input
                  type='checkbox'
                  className='form-check-input'
                  checked={!!sel[it.version_id]}
                  onChange={(e) => setSel((prev) => ({...prev, [it.version_id]: e.target.checked}))}
                />
                <span className='fw-bold'>{it.name || 'מודעה'}</span>
                <span className='badge fs-9' style={{background: sel[it.version_id] ? '#e6f4ea' : '#f1f3f5', color: sel[it.version_id] ? '#1e7d34' : '#868e96'}}>
                  {sel[it.version_id] ? 'ישוחזר' : 'מוחרג'}
                </span>
                {it.unchanged && (
                  <span className='badge fs-9' style={{background: '#f1f3f5', color: '#868e96'}} title='הגרסה בשינוי זה זהה לתוכן הנוכחי'>ללא שינוי</span>
                )}
              </label>
              <div className='d-flex align-items-center gap-2'>
                <div className='btn-group btn-group-sm'>
                  <button type='button' className={`btn btn-sm ${mode === 'edit' ? 'btn-primary' : 'btn-light'}`} onClick={() => setView((p) => ({...p, [it.version_id]: 'edit'}))}>עריכה</button>
                  <button type='button' className={`btn btn-sm ${mode === 'diff' ? 'btn-primary' : 'btn-light'}`} onClick={() => setView((p) => ({...p, [it.version_id]: 'diff'}))}>השוואה</button>
                </div>
                <span className='text-muted fs-8' dir='ltr'>{fmt(it.changed_at)}</span>
              </div>
            </div>
            {mode === 'diff' ? (
              <div>
                <div className='text-muted fs-8 mb-1'>
                  השוואה: <span style={{color: '#c0392b'}}>− נוכחי</span> → <span style={{color: '#1e7d34'}}>+ הגרסה שתשוחזר</span>
                </div>
                <div className='fs-8 p-2 rounded' style={{maxHeight: 320, overflow: 'auto', background: '#fbfbfd', border: '1px solid #e6e8ee'}} dir='rtl'>
                  {diffLines(it.after || '', restore).map((d, idx) => (
                    <div
                      key={idx}
                      style={{
                        whiteSpace: 'pre-wrap',
                        padding: '0 6px',
                        background: d.type === 'add' ? 'rgba(80,180,120,0.18)' : d.type === 'del' ? 'rgba(230,80,80,0.15)' : 'transparent',
                        color: d.type === 'del' ? '#a11722' : d.type === 'add' ? '#12591f' : '#333',
                      }}
                    >
                      <span style={{opacity: 0.45, marginInlineEnd: 6}}>{d.type === 'add' ? '+' : d.type === 'del' ? '−' : ' '}</span>
                      {d.text || ' '}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className='row g-3'>
                <div className='col-md-6'>
                  <div className='text-muted fs-8 mb-1'>גרסה שתשוחזר (ניתן לעריכה)</div>
                  <textarea
                    className='form-control form-control-sm'
                    rows={8}
                    value={restore}
                    onChange={(e) => setEdited((prev) => ({...prev, [it.version_id]: e.target.value}))}
                    style={{background: 'rgba(80,180,120,0.06)'}}
                  />
                </div>
                <div className='col-md-6'>
                  <div className='text-muted fs-8 mb-1'>נוכחי (יוחלף)</div>
                  <div className='fs-8 p-2 rounded' style={{whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto', background: 'rgba(230,131,80,0.08)', border: '1px solid rgba(230,131,80,0.25)'}}>
                    {it.after || '—'}
                  </div>
                </div>
              </div>
            )}
          </div>
          )
        })
      )}
    </>
  )
}

// Standalone modal wrapper around ReverseByDatePanel — used by the admin button on
// the campaigns page.
export function VnRevertModal({onClose}: {onClose: () => void}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 20,
        overflowY: 'auto',
      }}
    >
      <div onClick={(e) => e.stopPropagation()} className='card' dir='rtl' style={{maxWidth: 860, width: '100%', margin: 'auto'}}>
        <div className='card-header align-items-center'>
          <h3 className='card-title mb-0'>שחזור פוסטים לפי תאריך <span className='badge badge-light-danger fs-9 ms-1'>אדמין</span></h3>
          <button className='btn btn-sm btn-icon btn-light' onClick={onClose}>✕</button>
        </div>
        <div className='card-body'>
          <ReverseByDatePanel />
        </div>
      </div>
    </div>
  )
}
